'use client'

import { forwardRef, ReactNode, useState, useEffect } from 'react'
import { X, AlertTriangle, Info, CheckCircle2, Undo2 } from 'lucide-react'
import { safeAccess } from '../utils/security'

/**
 * BIRHAUS Dialog Component
 * 
 * Anti-confirmation dialog implementing BIRHAUS Principle #5: Undo over confirm
 * 
 * Instead of asking "Are you sure?", this dialog:
 * - Performs the action immediately
 * - Shows undo option with clear feedback
 * - Reduces cognitive load through direct action
 * - Provides Spanish-first messaging
 */

// BIRHAUS: Utility function (self-contained)
function cn(...classes: (string | undefined | null | false)[]): string {
  return classes.filter(Boolean).join(' ')
}

export interface BirhausDialogAction {
  id: string
  label: string
  labelEs?: string
  labelEn?: string
  onClick: () => void | Promise<void>
  variant?: 'primary' | 'secondary' | 'destructive'
  disabled?: boolean
  loading?: boolean
  icon?: ReactNode
}

export interface BirhausUndoConfig {
  enabled: boolean
  timeoutMs?: number
  undoAction: () => void | Promise<void>
  messageEs?: string
  messageEn?: string
  labelEs?: string
  labelEn?: string
}

export interface BirhausDialogProps {
  // Visibility control
  open?: boolean
  onOpenChange?: (open: boolean) => void
  
  // BIRHAUS Principle #7: Bilingual by design
  title?: string
  titleEs?: string
  titleEn?: string
  
  description?: string
  descriptionEs?: string
  descriptionEn?: string
  
  // Content
  children?: ReactNode
  
  // BIRHAUS Principle #5: Undo over confirm
  undoConfig?: BirhausUndoConfig
  
  // Dialog variants
  variant?: 'default' | 'success' | 'warning' | 'error' | 'info'
  
  // Actions (following 4-3-1 rule)
  primaryAction?: BirhausDialogAction
  secondaryActions?: BirhausDialogAction[] // Max 2-3 recommended
  
  // Layout options
  size?: 'sm' | 'md' | 'lg' | 'xl' | 'full'
  position?: 'center' | 'top' | 'bottom'
  
  // Behavior
  closeOnOverlayClick?: boolean
  closeOnEscape?: boolean
  persistent?: boolean
  
  // Accessibility
  role?: 'dialog' | 'alertdialog'
  ariaLabelledBy?: string
  ariaDescribedBy?: string
  
  // Styling
  className?: string
  overlayClassName?: string
}

export const BirhausDialog = forwardRef<HTMLDivElement, BirhausDialogProps>(({
  // Visibility
  open = false,
  onOpenChange,
  
  // Content (Spanish-first)
  title,
  titleEs,
  titleEn,
  description,
  descriptionEs,
  descriptionEn,
  children,
  
  // Undo functionality
  undoConfig,
  
  // Appearance
  variant = 'default',
  size = 'md',
  position = 'center',
  
  // Behavior
  closeOnOverlayClick = true,
  closeOnEscape = true,
  persistent = false,
  
  // Actions
  primaryAction,
  secondaryActions = [],
  
  // Accessibility
  role = 'dialog',
  ariaLabelledBy,
  ariaDescribedBy,
  
  // Styling
  className,
  overlayClassName
}, ref) => {
  
  // BIRHAUS: State management
  const [isUndoActive, setIsUndoActive] = useState(false)
  const [undoTimeRemaining, setUndoTimeRemaining] = useState(0)
  const [isUndoing, setIsUndoing] = useState(false)
  
  // BIRHAUS Principle #7: Spanish-first text resolution
  const resolveText = (spanish?: string, english?: string, fallback?: string) => {
    return spanish || english || fallback || ''
  }
  
  const displayTitle = resolveText(titleEs, titleEn, title)
  const displayDescription = resolveText(descriptionEs, descriptionEn, description)
  
  // BIRHAUS: Undo countdown effect
  useEffect(() => {
    if (!isUndoActive || !undoConfig?.enabled) return
    
    const timeout = undoConfig.timeoutMs || 10000 // 10 seconds default
    setUndoTimeRemaining(timeout)
    
    const interval = setInterval(() => {
      setUndoTimeRemaining(prev => {
        if (prev <= 1000) {
          setIsUndoActive(false)
          clearInterval(interval)
          return 0
        }
        return prev - 1000
      })
    }, 1000)
    
    return () => clearInterval(interval)
  }, [isUndoActive, undoConfig?.enabled, undoConfig?.timeoutMs])
  
  // BIRHAUS: Keyboard handling
  useEffect(() => {
    if (!open) return
    
    const handleKeyDown = (event: KeyboardEvent) => {
      if (event.key === 'Escape' && closeOnEscape && !persistent) {
        onOpenChange?.(false)
      }
    }
    
    document.addEventListener('keydown', handleKeyDown)
    return () => document.removeEventListener('keydown', handleKeyDown)
  }, [open, closeOnEscape, persistent, onOpenChange])
  
  // BIRHAUS: Handle undo action
  const handleUndo = async () => {
    if (!undoConfig?.undoAction) return
    
    setIsUndoing(true)
    try {
      await undoConfig.undoAction()
      setIsUndoActive(false)
      onOpenChange?.(false)
    } catch (error) {
      console.error('BIRHAUS Dialog: Undo action failed:', error)
    } finally {
      setIsUndoing(false)
    }
  }
  
  // BIRHAUS: Enhanced action handler with undo support
  const handleAction = async (action: BirhausDialogAction) => {
    if (action.disabled || action.loading) return
    
    try {
      await action.onClick()
      
      // If this is a destructive action and undo is configured, show undo option
      if (action.variant === 'destructive' && undoConfig?.enabled) {
        setIsUndoActive(true)
      } else {
        onOpenChange?.(false)
      }
    } catch (error) {
      console.error('BIRHAUS Dialog: Action failed:', error)
    }
  }
  
  // BIRHAUS: Warn about cognitive overload
  const actionCount = (primaryAction ? 1 : 0) + secondaryActions.length
  if (actionCount > 4) {
    console.warn(`BIRHAUS 4-3-1 Rule Warning: Dialog has ${actionCount} actions (recommended: max 4)`)
  }
  
  // BIRHAUS Principle #9: Consistency via tokens
  const sizeClasses = {
    sm: 'max-w-sm',
    md: 'max-w-md',
    lg: 'max-w-lg',
    xl: 'max-w-xl',
    full: 'max-w-full mx-4'
  }
  
  const positionClasses = {
    center: 'items-center justify-center',
    top: 'items-start justify-center pt-16',
    bottom: 'items-end justify-center pb-16'
  }
  
  const variantStyles = {
    default: {
      icon: null,
      headerBg: 'bg-gray-50',
      borderColor: 'border-gray-200'
    },
    success: {
      icon: <CheckCircle2 className="h-5 w-5 text-green-600" />,
      headerBg: 'bg-green-50',
      borderColor: 'border-green-200'
    },
    warning: {
      icon: <AlertTriangle className="h-5 w-5 text-yellow-600" />,
      headerBg: 'bg-yellow-50',
      borderColor: 'border-yellow-200'
    },
    error: {
      icon: <AlertTriangle className="h-5 w-5 text-red-600" />,
      headerBg: 'bg-red-50',
      borderColor: 'border-red-200'
    },
    info: {
      icon: <Info className="h-5 w-5 text-blue-600" />,
      headerBg: 'bg-blue-50',
      borderColor: 'border-blue-200'
    }
  }
  
  const currentVariant = safeAccess(variantStyles, variant, variantStyles.default)
  
  if (!open) return null
  
  // BIRHAUS: Undo overlay (Principle #5: Undo over confirm)
  if (isUndoActive && undoConfig?.enabled) {
    const undoMessage = resolveText(
      undoConfig.messageEs, 
      undoConfig.messageEn, 
      'Acción completada. Presiona deshacer si fue un error.'
    )
    const undoLabel = resolveText(
      undoConfig.labelEs,
      undoConfig.labelEn,
      'Deshacer'
    )
    
    return (
      <div className="fixed inset-0 z-50 bg-black/50 flex items-center justify-center p-4">
        <div 
          ref={ref}
          className={cn(
            'bg-white rounded-lg shadow-lg border-2 border-green-200 p-6 max-w-md w-full',
            className
          )}
          role="alertdialog"
          aria-labelledby="undo-title"
          aria-describedby="undo-description"
        >
          <div className="flex items-start gap-4">
            <div className="flex-shrink-0">
              <CheckCircle2 className="h-6 w-6 text-green-600" />
            </div>
            
            <div className="flex-1 min-w-0">
              <h3 id="undo-title" className="text-lg font-semibold text-gray-900 mb-2">
                ¡Acción completada!
              </h3>
              
              <p id="undo-description" className="text-sm text-gray-600 mb-4">
                {undoMessage}
              </p>
              
              <div className="flex items-center justify-between">
                <button
                  onClick={handleUndo}
                  disabled={isUndoing}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
                    'bg-red-600 text-white hover:bg-red-700 focus:outline-none focus:ring-2 focus:ring-red-500',
                    'disabled:pointer-events-none disabled:opacity-50'
                  )}
                >
                  {isUndoing ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white" />
                  ) : (
                    <Undo2 className="h-4 w-4" />
                  )}
                  {isUndoing ? 'Deshaciendo...' : undoLabel}
                </button>
                
                <div className="text-sm text-gray-500">
                  Auto-cierre en {Math.ceil(undoTimeRemaining / 1000)}s
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>
    )
  }
  
  return (
    <div 
      className={cn(
        'fixed inset-0 z-50 bg-black/50 flex',
        safeAccess(positionClasses, position, positionClasses.center),
        overlayClassName
      )}
      onClick={closeOnOverlayClick ? () => onOpenChange?.(false) : undefined}
    >
      <div 
        ref={ref}
        className={cn(
          'bg-white rounded-lg shadow-xl border',
          safeAccess(sizeClasses, size, sizeClasses.md),
          currentVariant.borderColor,
          'w-full overflow-hidden',
          className
        )}
        onClick={(e) => e.stopPropagation()}
        role={role}
        aria-labelledby={ariaLabelledBy || 'dialog-title'}
        aria-describedby={ariaDescribedBy || 'dialog-description'}
        data-birhaus-component="dialog"
        data-variant={variant}
      >
        {/* Header */}
        {(displayTitle || currentVariant.icon) && (
          <div className={cn(
            'px-6 py-4 border-b border-gray-200',
            currentVariant.headerBg
          )}>
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                {currentVariant.icon}
                {displayTitle && (
                  <h2 
                    id={ariaLabelledBy || 'dialog-title'}
                    className="text-lg font-semibold text-gray-900"
                  >
                    {displayTitle}
                  </h2>
                )}
              </div>
              
              {!persistent && (
                <button
                  onClick={() => onOpenChange?.(false)}
                  className="text-gray-400 hover:text-gray-600 focus:outline-none focus:ring-2 focus:ring-gray-500 rounded p-1"
                  aria-label="Cerrar diálogo"
                >
                  <X className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>
        )}
        
        {/* Content */}
        <div className="px-6 py-4">
          {displayDescription && (
            <p 
              id={ariaDescribedBy || 'dialog-description'}
              className="text-gray-600 mb-4"
            >
              {displayDescription}
            </p>
          )}
          
          {children}
        </div>
        
        {/* Actions */}
        {(primaryAction || secondaryActions.length > 0) && (
          <div className="px-6 py-4 bg-gray-50 border-t border-gray-200">
            <div className="flex items-center justify-end gap-3">
              {/* BIRHAUS Principle #3: One clear primary action */}
              {secondaryActions.map((action) => (
                <button
                  key={action.id}
                  onClick={() => handleAction(action)}
                  disabled={action.disabled || action.loading}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
                    'border border-gray-300 bg-white text-gray-700 hover:bg-gray-50',
                    'focus:outline-none focus:ring-2 focus:ring-gray-500',
                    'disabled:pointer-events-none disabled:opacity-50'
                  )}
                >
                  {action.loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                  ) : action.icon}
                  {action.labelEs || action.label}
                </button>
              ))}
              
              {primaryAction && (
                <button
                  onClick={() => handleAction(primaryAction)}
                  disabled={primaryAction.disabled || primaryAction.loading}
                  className={cn(
                    'inline-flex items-center gap-2 px-4 py-2 text-sm font-medium rounded-md',
                    'focus:outline-none focus:ring-2 focus:ring-offset-2',
                    'disabled:pointer-events-none disabled:opacity-50',
                    primaryAction.variant === 'primary' && 'bg-blue-600 text-white hover:bg-blue-700 focus:ring-blue-500',
                    primaryAction.variant === 'destructive' && 'bg-red-600 text-white hover:bg-red-700 focus:ring-red-500',
                    (!primaryAction.variant || primaryAction.variant === 'secondary') && 'bg-gray-600 text-white hover:bg-gray-700 focus:ring-gray-500'
                  )}
                >
                  {primaryAction.loading ? (
                    <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-current" />
                  ) : primaryAction.icon}
                  {primaryAction.labelEs || primaryAction.label}
                </button>
              )}
            </div>
          </div>
        )}
      </div>
    </div>
  )
})

BirhausDialog.displayName = 'BirhausDialog'

// BIRHAUS: Hook for managing dialog state with undo support
export function useBirhausDialog() {
  const [open, setOpen] = useState(false)
  const [undoActive, setUndoActive] = useState(false)
  
  const openDialog = () => setOpen(true)
  const closeDialog = () => setOpen(false)
  const activateUndo = () => setUndoActive(true)
  const deactivateUndo = () => setUndoActive(false)
  
  return {
    open,
    undoActive,
    openDialog,
    closeDialog,
    activateUndo,
    deactivateUndo,
    setOpen
  }
}

// BIRHAUS: Helper to create dialog actions with Spanish-first labels
export function createBirhausDialogAction(
  id: string,
  labelEs: string,
  onClick: () => void | Promise<void>,
  options?: {
    labelEn?: string
    icon?: ReactNode
    variant?: 'primary' | 'secondary' | 'destructive'
    disabled?: boolean
  }
): BirhausDialogAction {
  return {
    id,
    label: labelEs,
    labelEs,
    labelEn: options?.labelEn,
    onClick,
    icon: options?.icon,
    variant: options?.variant || 'secondary',
    disabled: options?.disabled || false,
    loading: false
  }
}

// Components and utilities are exported via individual export declarations above